import React from 'react';
import { Link } from 'react-router-dom';
import { ThumbsUp, MessageSquare, Clock, Bell, CheckCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { BookIdea } from '../hooks/useBookIdeas';

type BookIdeaCardProps = {
  idea: BookIdea;
};

export default function BookIdeaCard({ idea }: BookIdeaCardProps) {
  // Combine genres and custom tags into a single array
  const allTags = [...idea.genre, ...(idea.custom_tags || [])];

  // Determine if this is a main profile or pen name
  const isMainProfile = idea.identity_id === idea.author_id;

  return (
    <article className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:scale-[1.02] relative">
      {idea.has_published_milestone && (
        <div className="absolute top-2 right-2 px-2 py-1 bg-green-100 text-green-800 rounded-full flex items-center gap-1 text-sm z-10">
          <CheckCircle className="w-4 h-4" />
          <span>Published</span>
        </div>
      )}
      
      {idea.cover_image && (
        <div className="relative h-48">
          <img
            src={idea.cover_image}
            alt={`Cover for ${idea.title}`}
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <div className="p-6">
        <div className="flex items-start justify-between mb-2">
          <Link 
            to={isMainProfile ? `/profile/${idea.identity_id}` : `/pen-name/${idea.identity_id}`}
            className="flex items-center gap-2 hover:text-indigo-600"
          >
            {idea.identity_avatar_url ? (
              <img
                src={idea.identity_avatar_url}
                alt={idea.identity_username}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 bg-indigo-100 rounded-full" />
            )}
            <div>
              <span className="font-medium span-user">{idea.identity_username}</span>
              <div className="text-sm text-gray-500 flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {formatDistanceToNow(new Date(idea.created_at), { addSuffix: true })}
              </div>
            </div>
          </Link>

          <div className="flex items-center gap-1 text-gray-500">
            <Bell className="w-5 h-5" />
            <span>{idea.followers_count || 0}</span>
          </div>
        </div>
        
        <Link to={`/idea/${idea.id}`}>
          <h2 className="text-xl font-bold bt-title hover:bg-title mb-4">{idea.title}</h2>
        </Link>
        
        <p className="text-gray-600 mb-4 line-clamp-2">{idea.description}</p>
        
        <div className="flex flex-wrap gap-2 mb-4">
          {allTags.map((tag) => (
            <Link
              key={tag}
              to={`/?search=${encodeURIComponent(tag)}`}
              className="px-2 py-1 text-sm bg-indigo-50 text-indigo-700 rounded-full hover:bg-hover"
            >
              {tag}
            </Link>
          ))}
        </div>

        <div className="flex items-center justify-end gap-4 text-sm text-gray-500">
          <div className="flex items-center gap-1">
            <ThumbsUp className="w-4 h-4" />
            <span>{idea.votes_count}</span>
          </div>
          <div className="flex items-center gap-1">
            <MessageSquare className="w-4 h-4" />
            <span>{idea.comments_count}</span>
          </div>
        </div>
      </div>
    </article>
  );
}